import Stack from '@mui/material/Stack'
import { useContext } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router-dom'

import { GapBox } from 'components/Box'
import { Switch, TextField } from 'components/Form'
import { BoxForm } from 'components/Form/BoxForm/BoxForm'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { RelatedList, RelatedListItem, RelatedSettings } from 'components/RelatedSettings'
import { AddEditAdUnitContext } from 'features/inventory/ad-unit/contexts/AddEditAdUnitContext'
import { useAbility } from 'providers/casl'
import { CONTAINER_SPACING } from 'providers/material-ui/theme/constants'
import { ROUTES } from 'routes'

import type { Schema } from '../schema'

export const BasicSettings = (): React.JSX.Element => {
  const { t } = useTranslation('features/adUnit', {
    keyPrefix: 'form.unfilledRecovery.basicSettings',
  })

  const { adUnit } = useContext(AddEditAdUnitContext)
  const { control, watch } = useFormContext<Schema>()
  const ability = useAbility()
  const { customizeBaseAdUnitPathEnabled } = watch()
  const cannotUpdate: boolean =
    ability.cannot('update', 'AdUnit') ||
    ability.cannot('update', 'AdUnitFeature-unfilledRecoveryBasicSettings') ||
    ability.cannot('update', 'Product-unfilledRecovery')

  if (!adUnit) {
    throw Error('Ad unit should be accessible')
  }

  const {
    domain: {
      domainUnfilledRecovery: { baseAdUnitPath: domainBaseAdUnitPath },
      id: domainId,
      workspace: { id: workspaceId },
    },
  } = adUnit

  const domainSettingsUrl: string = generatePath(ROUTES.INVENTORY.DOMAIN.EDIT, {
    id: domainId,
    tab: 'unfilled-recovery',
    workspaceId,
  })

  return (
    <Paper
      data-cy='section-basic-settings'
      variant='cream'
    >
      <SectionHeader
        subtitle={t('description')}
        title={t('title')}
      />

      <GapBox />

      <BoxForm>
        <Switch
          control={control}
          disabled={cannotUpdate}
          label={t('overrideDomainSettings.label')}
          labelPlacement='start'
          name='customizeBaseAdUnitPathEnabled'
        />

        {customizeBaseAdUnitPathEnabled && (
          <Stack
            direction='row'
            spacing={CONTAINER_SPACING}
          >
            <TextField
              control={control}
              disabled={cannotUpdate}
              label={t('baseAdUnitPath.label')}
              name='baseAdUnitPath'
            />

            <GapBox />

            {domainBaseAdUnitPath && (
              <RelatedSettings title={t('relatedSettings.title')}>
                <RelatedList>
                  <RelatedListItem
                    content={t('relatedSettings.content')}
                    url={domainSettingsUrl}
                    value={domainBaseAdUnitPath}
                  />
                </RelatedList>
              </RelatedSettings>
            )}
          </Stack>
        )}
      </BoxForm>
    </Paper>
  )
}
